import { AbsoluteFill, OffthreadVideo } from 'remotion';
import type { AppConfig } from '../lib/types';
import { useComposition } from './useComposition';

export const MainComposition: React.FC<AppConfig> = (config) => {
  const {
    textColor,
    backgroundColor,
    backgroundImage,
    backgroundVideoUrl,
    fontName,
    fontSize,
    textAlign,
    lineHeight,
    textShadow,
    blur,
    vignette,
  } = config;

  const containerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: textColor,
    fontFamily: `"${fontName}", sans-serif`,
    position: 'relative',
    overflow: 'hidden',
  };

  const backgroundStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: `blur(${(blur / 100) * 64}px)`,
  };

  const vignetteStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `radial-gradient(circle, transparent ${100 - Math.abs(vignette)}%, rgba(${vignette > 0 ? '0,0,0' : '255,255,255'},${(Math.abs(vignette) / 100) * 0.8}) 100%)`,
    pointerEvents: 'none',
  };

  const textStyle: React.CSSProperties = {
    fontSize: `${fontSize}px`,
    fontWeight: 'bold',
    whiteSpace: 'pre-wrap',
    textAlign,
    lineHeight,
    textShadow: textShadow > 0 ? `0 0 ${textShadow}px rgba(0,0,0,0.8)` : 'none',
    padding: '0 10%',
    width: '100%',
    zIndex: 2,
  };

  const { renderContent, renderAudio } = useComposition(config, textStyle);

  return (
    <AbsoluteFill style={{ ...containerStyle, backgroundColor: undefined }}>
      <AbsoluteFill style={{ backgroundColor }} />

      {backgroundVideoUrl ? (
        <OffthreadVideo
          src={backgroundVideoUrl}
          style={{ ...backgroundStyle, zIndex: 0 }}
          muted
        />
      ) : backgroundImage ? (
        <img src={backgroundImage} style={{ ...backgroundStyle, zIndex: 0 }} alt="" />
      ) : null}

      <div style={{ ...vignetteStyle, zIndex: 1 }} />

      <AbsoluteFill style={{ 
        zIndex: 2, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        pointerEvents: 'none',
      }}>
        {renderContent()}
      </AbsoluteFill>

      {renderAudio()}
    </AbsoluteFill>
  );
};
